import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthLayout from '@shared/layouts/AuthLayout';
import './CookiePolicy.css';

const CookiePolicy: React.FC = () => {
  const { t } = useTranslation('legal');

  const tocItems = t('cookiePolicy.tocItems', { returnObjects: true }) as string[];
  const cookiesTable = t('cookiePolicy.cookiesTable', { returnObjects: true }) as Array<{
    name: string;
    purpose: string;
    duration: string;
  }>;
  const sections = t('cookiePolicy.sections', { returnObjects: true }) as Array<{
    id: string;
    number: string;
    title: string;
    paragraphs: string[];
    listItems: string[];
  }>;

  return (
    <AuthLayout maxWidth="max-w-4xl">
      <div className="bg-surface-card-light dark:bg-surface-card-dark rounded-3xl border border-surface-border-light dark:border-surface-border-dark shadow-soft p-8 sm:p-12">
        <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight mb-2">
          {t('cookiePolicy.title')}
        </h1>
        <p className="text-xs font-bold text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest mb-8">
          {t('cookiePolicy.lastUpdated')}
        </p>

        <div className="space-y-8 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
          <p>
            {t('cookiePolicy.intro')} {t('cookiePolicy.privacyLinkPrompt')}{' '}
            <Link
              to="/privacy"
              className="text-brand-600 dark:text-brand-400 font-semibold hover:underline"
            >
              {t('cookiePolicy.footer.privacyPolicy')}
            </Link>
            .
          </p>

          {/* Table of Contents */}
          <div>
            <h2 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider mb-3">
              {t('cookiePolicy.tableOfContentsTitle')}
            </h2>
            <ul className="list-disc space-y-1 pl-5 text-slate-600 dark:text-slate-300">
              {tocItems.map((item, index) => (
                <li key={sections[index]?.id ?? item}>
                  <a
                    href={`#${sections[index]?.id ?? ''}`}
                    className="block min-h-11 rounded-sm py-3 text-brand-600 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 dark:text-brand-400 dark:focus-visible:ring-brand-400"
                  >
                    {item}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Cookies Table */}
          <div className="cookie-policy-table mt-4 overflow-hidden rounded-xl border border-surface-border-light dark:border-surface-border-dark">
            <table
              className="w-full text-sm"
              aria-label={t('cookiePolicy.tableHeaders.cookieStorage', 'Browser data')}
            >
              <thead>
                <tr className="bg-surface-2-light dark:bg-surface-2-dark">
                  <th className="text-left p-3 font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider">
                    {t('cookiePolicy.tableHeaders.cookieStorage', 'Cookie / Storage')}
                  </th>
                  <th className="text-left p-3 font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider">
                    {t('cookiePolicy.tableHeaders.purpose', 'Purpose')}
                  </th>
                  <th className="text-left p-3 font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider">
                    {t('cookiePolicy.tableHeaders.duration', 'Duration')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border-light dark:divide-surface-border-dark">
                {cookiesTable.map((cookie, index) => (
                  <tr key={index}>
                    <td className="p-3 font-mono text-xs">{cookie.name}</td>
                    <td className="p-3">{cookie.purpose}</td>
                    <td className="p-3">{cookie.duration}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ul
            className="cookie-policy-cards mt-4 space-y-3"
            aria-label={t('cookiePolicy.tableHeaders.cookieStorage', 'Browser data')}
          >
            {cookiesTable.map((cookie) => (
              <li
                key={cookie.name}
                className="rounded-xl border border-surface-border-light bg-surface-2-light p-4 dark:border-surface-border-dark dark:bg-surface-2-dark"
              >
                <h3 className="break-all font-mono text-sm font-semibold text-slate-900 dark:text-white">
                  {cookie.name}
                </h3>
                <dl className="mt-3 space-y-3 text-sm leading-relaxed">
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-text-muted-light dark:text-text-muted-dark">
                      {t('cookiePolicy.tableHeaders.purpose', 'Purpose')}
                    </dt>
                    <dd className="mt-1 text-slate-600 dark:text-slate-300">{cookie.purpose}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-text-muted-light dark:text-text-muted-dark">
                      {t('cookiePolicy.tableHeaders.duration', 'Duration')}
                    </dt>
                    <dd className="mt-1 text-slate-600 dark:text-slate-300">{cookie.duration}</dd>
                  </div>
                </dl>
              </li>
            ))}
          </ul>

          {/* Sections */}
          {sections.map((section) => (
            <section key={section.id} id={section.id} className="scroll-mt-6">
              <h2 className="text-lg font-black text-slate-900 dark:text-white tracking-tight mb-3">
                {section.number}. {section.title}
              </h2>
              {section.paragraphs.map((paragraph, pIndex) => (
                <p key={pIndex} className={pIndex > 0 ? 'mt-3' : undefined}>
                  {paragraph}
                </p>
              ))}
              {section.listItems.length > 0 && (
                <ul className="list-disc list-inside space-y-1 mt-2 ml-4">
                  {section.listItems.map((item, liIndex) => (
                    <li key={liIndex}>{item}</li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        {/* Footer navigation */}
        <div className="mt-10 pt-8 border-t border-surface-border-light dark:border-surface-border-dark flex flex-wrap gap-6 text-xs font-bold text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest">
          <Link
            to="/terms"
            className="hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
          >
            {t('cookiePolicy.footer.termsOfService')}
          </Link>
          <Link
            to="/privacy"
            className="hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
          >
            {t('cookiePolicy.footer.privacyPolicy')}
          </Link>
        </div>
      </div>
    </AuthLayout>
  );
};

export default CookiePolicy;
